import React, {createContext, useState, useEffect, ReactNode} from "react";
import lscache from "lscache";

interface IVideoPlayerSettingsContext {
    isTheatreMode: boolean;
    setIsTheatreMode: React.Dispatch<React.SetStateAction<boolean>>;
    volume: number;
    setVolume: React.Dispatch<React.SetStateAction<number>>;
    muted: boolean;
    setMuted: React.Dispatch<React.SetStateAction<boolean>>;
    subtitlesEnabled: boolean;
    setSubtitlesEnabled: React.Dispatch<React.SetStateAction<boolean>>;
    subtitlesLanguage: string;
    setSubtitlesLanguage: React.Dispatch<React.SetStateAction<string>>;
}

export const VideoPlayerSettingsContext = createContext<IVideoPlayerSettingsContext>({} as IVideoPlayerSettingsContext);

interface Props {
    children: ReactNode;
}

export const VideoPlayerSettingsProvider: React.FC<Props> = ({children}) => {
    // Set expiration to one year (525600 minutes)
    const
        expiration = 525600,

        // Initialize isTheatreMode from lscache (default: false)
        cachedTheatreMode = lscache.get("isTheatreMode"),
        initialTheatreMode =
            typeof cachedTheatreMode !== "undefined" && cachedTheatreMode !== null
                ? cachedTheatreMode === true || cachedTheatreMode === "true"
                : false,
        [isTheatreMode, setIsTheatreMode] = useState<boolean>(initialTheatreMode),

        // Initialize volume from lscache (default: 1)
        cachedVolume = lscache.get("volume");

    let initialVolume = 1;
    if (typeof cachedVolume !== "undefined" && cachedVolume !== null) {
        const parsedVolume = parseFloat(String(cachedVolume));
        if (isFinite(parsedVolume)) {
            initialVolume = parsedVolume;
        }
    }

    const
        [volume, setVolume] = useState<number>(initialVolume),

        // Initialize muted from lscache (default: false)
        cachedMuted = lscache.get("muted"),
        initialMuted =
            typeof cachedMuted !== "undefined" && cachedMuted !== null
                ? cachedMuted === true || cachedMuted === "true"
                : false,
        [muted, setMuted] = useState<boolean>(initialMuted),

        // Initialize subtitlesEnabled from lscache (default: false)
        cachedSubtitlesEnabled = lscache.get("subtitlesEnabled"),
        initialSubtitlesEnabled =
            typeof cachedSubtitlesEnabled !== "undefined" && cachedSubtitlesEnabled !== null
                ? cachedSubtitlesEnabled === true || cachedSubtitlesEnabled === "true"
                : false,
        [subtitlesEnabled, setSubtitlesEnabled] = useState<boolean>(initialSubtitlesEnabled),

        // Initialize subtitlesLanguage from lscache (default: "" — no saved language)
        cachedSubtitlesLanguage = lscache.get("subtitlesLanguage"),
        initialSubtitlesLanguage =
            typeof cachedSubtitlesLanguage === "string" ? cachedSubtitlesLanguage : "",
        [subtitlesLanguage, setSubtitlesLanguage] = useState<string>(initialSubtitlesLanguage);

    // Update lscache when values change
    useEffect(() => {
        lscache.set("isTheatreMode", isTheatreMode, expiration);
    }, [isTheatreMode]);

    useEffect(() => {
        lscache.set("volume", volume, expiration);
    }, [volume]);

    useEffect(() => {
        lscache.set("muted", muted, expiration);
    }, [muted]);

    useEffect(() => {
        lscache.set("subtitlesEnabled", subtitlesEnabled, expiration);
    }, [subtitlesEnabled]);

    useEffect(() => {
        lscache.set("subtitlesLanguage", subtitlesLanguage, expiration);
    }, [subtitlesLanguage]);

    return (
        <VideoPlayerSettingsContext.Provider
            value={{
                isTheatreMode,
                setIsTheatreMode,
                volume,
                setVolume,
                muted,
                setMuted,
                subtitlesEnabled,
                setSubtitlesEnabled,
                subtitlesLanguage,
                setSubtitlesLanguage,
            }}
        >
            {children}
        </VideoPlayerSettingsContext.Provider>
    );
};
